import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import { LayoutGrid, ShieldCheck, LogOut, Map, CalendarDays, BookOpen } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import Footer from "@/components/Footer";
import ProfileCompletionBanner from "@/components/ProfileCompletionBanner";

const isSectionActive = (to, pathname) => {
  if (to === "/app") {
    return pathname === "/app" || /^\/app\/(members|kecamatan|businesses|pengurus)(\/|$)/.test(pathname);
  }
  return pathname === to || pathname.startsWith(to + "/");
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const onLogout = async () => { await logout(); nav("/"); };

  const links = [
    { to: "/app", label: "Direktori", icon: LayoutGrid, testId: "nav-directory" },
    { to: "/app/map", label: "Peta", icon: Map, testId: "nav-map" },
    { to: "/app/events", label: "Event", icon: CalendarDays, testId: "nav-events" },
    { to: "/app/blog", label: "Blog", icon: BookOpen, testId: "nav-blog" },
  ];
  const canSeeAdmin = user?.role === "admin" || user?.role === "pengurus";
  if (canSeeAdmin) {
    links.push({ to: "/admin", label: "Admin", icon: ShieldCheck, testId: "nav-admin" });
  }

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden bg-bizma-cream text-bizma-ink pb-20 md:pb-0" data-testid="layout-root">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-bizma-cream/80 border-b border-bizma-border shrink-0">
        <div className="container-x flex items-center justify-between h-16">
          <Link to="/app" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo-bizma.png" alt="BIZMA" className="w-9 h-9 rounded-full object-cover shadow-sm" />
            <div className="font-display font-bold text-xl tracking-wider">BIZMA</div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {links.map(l => {
              const active = isSectionActive(l.to, loc.pathname);
              return (
                <Link key={l.to} to={l.to}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? "bg-bizma-terracotta text-white" : "text-bizma-ink hover:bg-bizma-muted"}`}
                  data-testid={l.testId}>
                  <l.icon size={16} /> {l.label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-1 sm:gap-2">
            <NotificationBell />
            <Link to="/app/me" className="flex items-center gap-2" data-testid="header-user">
              {user?.photo_path ? (
                <img src={fileUrl(user.photo_path)} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-bizma-sage" />
              ) : user?.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-bizma-sage" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-bizma-sage text-white grid place-items-center font-semibold">
                  {(user?.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
            </Link>
            <button onClick={onLogout} className="text-bizma-subtext hover:text-bizma-terracotta transition-colors p-2" title="Keluar" data-testid="logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 container-x py-6 md:py-10 animate-fade-in min-w-0 w-full">
        <ProfileCompletionBanner />
        {children}
      </main>
      <Footer />

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-bizma-border" data-testid="mobile-bottom-nav">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${links.length}, minmax(0, 1fr))` }}>
          {links.map(l => {
            const active = isSectionActive(l.to, loc.pathname);
            return (
              <Link key={l.to} to={l.to}
                className={`flex flex-col items-center gap-1 py-3 text-xs ${active ? "text-bizma-terracotta font-semibold" : "text-bizma-subtext"}`}
                data-testid={`mobile-${l.testId}`}>
                <l.icon size={20} /> {l.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
